import { Link } from "react-router-dom";
import { ChevronRight, Leaf, Award, Heart, Truck } from "lucide-react";

export const AboutPage = () => {
  return (
    <div className="animate-fade-in">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4">
          <nav className="flex items-center gap-2 text-sm font-sans" data-testid="breadcrumb">
            <Link to="/" className="text-stone-500 hover:text-deep-moss">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <span className="text-stone-900">About</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative h-[50vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1559749619-5f290507d5e6?w=1600"
          alt="WEH International store"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center text-center">
          <div>
            <p className="font-accent text-sm text-bone-cream/80 uppercase tracking-widest mb-3">
              About Us
            </p>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-white">
              WEH International
            </h1>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-2">
                Our Story
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900 mb-6">
                Your Global Marketplace
              </h2>
              <div className="space-y-4 font-sans text-stone-600 leading-relaxed">
                <p>
                  WEH International is your global marketplace providing you with gift items, jams, jellies, 
                  jewelry, organic herbal products and teas, as well as rare exotic plants.
                </p>
                <p>
                  With over 25 years of sourcing and growing plants from all corners of the world, we've 
                  gained a wealth of knowledge and experience that we love sharing with you. Our mission 
                  is to help you get your start growing and caring for your plants with greater success.
                </p>
                <p>
                  Every product in our store is carefully selected to bring you the best quality. From 
                  our hand-picked herbal teas to our homemade jellies, we put care and attention into 
                  everything we offer.
                </p>
              </div>
            </div>
            <div className="aspect-square overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1459411552884-841db9b3cc2a?w=800"
                alt="Rare plants collection"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-2">
              What We Believe In
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900">
              Our Values
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: Leaf,
                title: "Quality First",
                description: "We source only the finest organic products and healthiest plants.",
              },
              {
                icon: Award,
                title: "Expertise",
                description: "25+ years of experience in plants and herbal products.",
              },
              {
                icon: Heart,
                title: "Customer Care",
                description: "We're here to answer your questions and help you succeed.",
              },
              {
                icon: Truck,
                title: "Reliable Delivery",
                description: "Safe shipping to get your products to you in perfect condition.",
              },
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center p-6" data-testid={`value-${index}`}>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-bone-cream flex items-center justify-center">
                    <Icon className="w-7 h-7 text-deep-moss" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-lg font-medium text-stone-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="font-sans text-sm text-stone-600">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-deep-moss text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">
            Ready to Explore?
          </h2>
          <p className="font-sans text-bone-cream/80 mb-8 max-w-md mx-auto">
            Browse our collection of herbal products, rare plants, and homemade goods.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/shop"
              data-testid="about-shop-btn"
              className="bg-white text-deep-moss hover:bg-bone-cream rounded-full px-8 py-3 font-sans font-medium transition-colors"
            >
              Shop Now
            </Link>
            <Link
              to="/contact"
              data-testid="about-contact-btn"
              className="border border-white text-white hover:bg-white hover:text-deep-moss rounded-full px-8 py-3 font-sans font-medium transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
